/**
 * StoreRevisionDebugOverlay — dev-only floating widget that shows live
 * revision counters / item counts for every Zustand store, plus a rolling
 * "flashes in last 10s" rate so we can quantify how often a store
 * re-renders during a slow interaction.
 *
 * Toggle with Ctrl+Shift+D (or Cmd+Shift+D on Mac).
 * Persistent across reloads via localStorage("khays_debug_overlay_open").
 *
 * GATING: Only mounts when running in dev (import.meta.env.DEV) OR the
 * current user is the Owner. Regular visitors and staff never see it
 * even if they hit the keyboard shortcut.
 */
import { useEffect, useRef, useState } from "react";
import { Activity, X, Pin, PinOff } from "lucide-react";

import { useAboutStore } from "@/stores/aboutStore";
import { useAdSlotsStore } from "@/stores/adSlotsStore";
import { useAdminSettings } from "@/stores/adminStore";
import { useConditionGuideStore } from "@/stores/conditionGuideStore";
import { useDeviceSettingsStore } from "@/stores/deviceSettingsStore";
import { useFaqStore } from "@/stores/faqStore";
import { useFeaturedStore } from "@/stores/featuredDeviceStore";
import { useFlashSaleStore } from "@/stores/flashSaleStore";
import { useMediaStore } from "@/stores/mediaStore";
import { useReviewStore } from "@/stores/reviewStore";
import { useShippingStore } from "@/stores/shippingStore";
import { useStockStore } from "@/stores/stockStore";
import { useStorefrontSettings } from "@/stores/storefrontSettingsStore";
import { useStorefrontTheme } from "@/stores/storefrontThemeStore";
import { useSwapStore } from "@/stores/swapStore";
import { useTutorialVideoStore } from "@/stores/tutorialVideoStore";
import { useOrdersRevision } from "@/data/orderStore";
import { useEnquiriesRevision } from "@/data/enquiryStore";
import { useSync } from "@/components/CloudSyncProvider";

const STORAGE_KEY = "khays_debug_overlay_open";
const PIN_KEY = "khays_debug_overlay_pinned";
const FLASH_WINDOW_MS = 10_000;

interface Row {
  name: string;
  rev: number | string;
}

/**
 * Tracks flash events (rev changes) within a rolling 10s window.
 * Returns { flash, count } where flash is true briefly after a change
 * and count is the number of bumps in the last FLASH_WINDOW_MS.
 */
function useFlashRate(rev: number | string) {
  const [flash, setFlash] = useState(false);
  const [count, setCount] = useState(0);
  const prevRef = useRef(rev);
  const timestampsRef = useRef<number[]>([]);

  useEffect(() => {
    if (prevRef.current !== rev) {
      prevRef.current = rev;
      timestampsRef.current.push(Date.now());
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 600);
      // Prune + update count immediately
      const cutoff = Date.now() - FLASH_WINDOW_MS;
      timestampsRef.current = timestampsRef.current.filter(ts => ts >= cutoff);
      setCount(timestampsRef.current.length);
      return () => clearTimeout(t);
    }
  }, [rev]);

  // Periodic pruner so the counter ticks down even with no new bumps
  useEffect(() => {
    const interval = setInterval(() => {
      const cutoff = Date.now() - FLASH_WINDOW_MS;
      const before = timestampsRef.current.length;
      timestampsRef.current = timestampsRef.current.filter(ts => ts >= cutoff);
      if (timestampsRef.current.length !== before) {
        setCount(timestampsRef.current.length);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return { flash, count };
}

function StoreRow({ name, rev }: { name: string; rev: number | string }) {
  const { flash, count } = useFlashRate(rev);
  // Color the rate badge by intensity
  const rateClass =
    count === 0
      ? "text-muted-foreground/50"
      : count < 3
        ? "text-foreground/70"
        : count < 8
          ? "text-amber-500"
          : "text-destructive font-bold";
  return (
    <div
      className={`flex items-center justify-between gap-2 px-2 py-1 rounded text-[10px] font-mono transition-colors ${
        flash ? "bg-primary/30 text-primary-foreground" : "text-foreground/80"
      }`}
    >
      <span className="truncate flex-1">{name}</span>
      <span className={`tabular-nums shrink-0 ${flash ? "font-bold" : ""}`}>{rev}</span>
      <span
        className={`tabular-nums shrink-0 w-8 text-right ${rateClass}`}
        title={`Bumps in last ${FLASH_WINDOW_MS / 1000}s`}
      >
        {count}/10s
      </span>
    </div>
  );
}

const StoreRevisionDebugOverlayInner = () => {
  const [open, setOpen] = useState<boolean>(() => localStorage.getItem(STORAGE_KEY) === "1");
  const [pinned, setPinned] = useState<boolean>(() => localStorage.getItem(PIN_KEY) === "1");

  // Keyboard shortcut (Ctrl/Cmd + Shift + D) to toggle
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "d") {
        e.preventDefault();
        setOpen(v => {
          const next = !v;
          localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
          return next;
        });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Subscribe to every Zustand store's revision counter (or a stable item
  // count when the store doesn't expose _rev). All hooks unconditional.
  const aboutRev = useAboutStore(s => (s as any)._rev ?? 0);
  const adSlotsRev = useAdSlotsStore(s => (s as any)._rev ?? s.slots.length);
  const adminRev = useAdminSettings(s => (s as any)._rev ?? 0);
  const conditionRev = useConditionGuideStore(s => s._rev);
  const deviceRev = useDeviceSettingsStore(s => (s as any)._rev ?? 0);
  const faqRev = useFaqStore(s => (s as any)._rev ?? 0);
  const featuredRev = useFeaturedStore(s => (s as any)._rev ?? 0);
  const flashSaleRev = useFlashSaleStore(s => s._rev);
  const mediaRev = useMediaStore(s => (s as any)._rev ?? 0);
  const reviewRev = useReviewStore(s => s._rev);
  const shippingRev = useShippingStore(s => s._rev);
  const stockRev = useStockStore(s => (s as any)._rev ?? 0);
  const sfSettingsRev = useStorefrontSettings(s => (s as any)._rev ?? 0);
  const sfThemeRev = useStorefrontTheme(s => (s as any)._rev ?? 0);
  const swapRev = useSwapStore(s => (s as any)._rev ?? 0);
  const tutorialRev = useTutorialVideoStore(s => (s as any)._rev ?? 0);
  const ordersRev = useOrdersRevision();
  const enquiriesRev = useEnquiriesRevision();

  if (!open) return null;

  const rows: Row[] = [
    { name: "about", rev: aboutRev },
    { name: "adSlots", rev: adSlotsRev },
    { name: "admin", rev: adminRev },
    { name: "conditionGuide", rev: conditionRev },
    { name: "deviceSettings", rev: deviceRev },
    { name: "enquiries", rev: enquiriesRev },
    { name: "faq", rev: faqRev },
    { name: "featured", rev: featuredRev },
    { name: "flashSale", rev: flashSaleRev },
    { name: "media", rev: mediaRev },
    { name: "orders", rev: ordersRev },
    { name: "review", rev: reviewRev },
    { name: "shipping", rev: shippingRev },
    { name: "stock", rev: stockRev },
    { name: "storefrontSettings", rev: sfSettingsRev },
    { name: "storefrontTheme", rev: sfThemeRev },
    { name: "swap", rev: swapRev },
    { name: "tutorialVideo", rev: tutorialRev },
  ];

  return (
    <div
      className={`fixed z-[9999] bottom-3 right-3 w-72 max-h-[70vh] bg-card/95 backdrop-blur border border-border rounded-lg shadow-2xl overflow-hidden ${
        pinned ? "" : "opacity-90 hover:opacity-100"
      }`}
      style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}
    >
      <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 border-b border-border bg-muted/50">
        <div className="flex items-center gap-1.5">
          <Activity className="w-3 h-3 text-primary" />
          <span className="text-[10px] font-bold text-foreground uppercase tracking-wide">Store Revisions</span>
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => {
              const next = !pinned;
              setPinned(next);
              localStorage.setItem(PIN_KEY, next ? "1" : "0");
            }}
            className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
            title={pinned ? "Unpin (auto-fade)" : "Pin (always opaque)"}
          >
            {pinned ? <Pin className="w-2.5 h-2.5" /> : <PinOff className="w-2.5 h-2.5" />}
          </button>
          <button
            onClick={() => {
              setOpen(false);
              localStorage.setItem(STORAGE_KEY, "0");
            }}
            className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
            title="Close (Ctrl+Shift+D to reopen)"
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 px-2.5 py-1 border-b border-border bg-muted/20 text-[9px] font-mono uppercase tracking-wide text-muted-foreground">
        <span className="flex-1">store</span>
        <span className="shrink-0">rev</span>
        <span className="shrink-0 w-8 text-right">rate</span>
      </div>
      <div className="overflow-y-auto max-h-[calc(70vh-3.5rem)] py-1 px-1 space-y-0.5">
        {rows.map(r => (
          <StoreRow key={r.name} name={r.name} rev={r.rev} />
        ))}
        <div className="border-t border-border mt-1 pt-1 px-1">
          <p className="text-[9px] text-muted-foreground leading-tight">
            <span className="font-bold">rev</span> = total bumps. <span className="font-bold">rate</span> = bumps in last 10s.
            Amber/red = hot. If a store is hot during a slow interaction, it's the culprit.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Gating wrapper — only mounts the overlay (and its 18 store subscriptions)
 * when in dev mode OR the current user is the Owner. This prevents regular
 * visitors from ever seeing it, even if they hit Ctrl+Shift+D.
 */
const StoreRevisionDebugOverlay = () => {
  const { isOwner } = useSync();
  const isDev = import.meta.env.DEV;
  if (!isDev && !isOwner) return null;
  return <StoreRevisionDebugOverlayInner />;
};

export default StoreRevisionDebugOverlay;
